import './game-card.scss';

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '@/app/store/hooks';
import { feedActions } from '@/entities/activity';
import MapArrowRightIcon from '@/shared/assets/icons/map-arrow-right.svg?react';
import { Badge } from '@/shared/ui/Badge';

type GameCardProps = {
  id: string;
  route: string;
  badge: string;
  badgeColor: string;
  accent: string;
  accentAlt: string;
  title: string;
  subtitle: string;
  delay: number;
  disabled: boolean;
  image?: string;
  shortDesc?: string;
};

export function GameCard({
  id,
  route,
  badge,
  badgeColor,
  accent,
  accentAlt,
  title,
  subtitle,
  delay,
  disabled,
  image,
  shortDesc,
}: GameCardProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClick = useCallback(() => {
    if (!disabled) {
      dispatch(feedActions.setLastPlayed(id));
      navigate(route);
    }
  }, [disabled, dispatch, id, navigate, route]);

  const classes = [
    `game-card game-card--${id}`,
    disabled ? 'game-card--disabled' : '',
    !disabled ? 'game-card--tappable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={classes}
      style={
        {
          '--card-accent': accent,
          '--card-accent-alt': accentAlt,
          '--card-in-delay': `${delay}s`,
        } as React.CSSProperties
      }
      onClick={handleClick}>
      <div className="game-card__glow" />
      <div className="game-card__shine-border" aria-hidden="true">
        <div className="game-card__shine-light" />
        <div className="game-card__shine-light" />
      </div>

      <div className="game-card__left">
        <div className="game-card__icon-wrap">
          {image && <img src={image} alt="" className="game-card__lottie" />}
        </div>
      </div>

      <div className="game-card__body">
        <div className="game-card__top">
          <span className="game-card__title">{title}</span>
          <Badge label={badge} variant={badgeColor as 'green' | 'amber' | 'gray'} />
        </div>
        <span className="game-card__subtitle">{subtitle}</span>
        {shortDesc && <span className="game-card__short-desc">{shortDesc}</span>}
      </div>

      <div className="game-card__arrow">
        {!disabled && <MapArrowRightIcon width={24} height={24} />}
      </div>
    </button>
  );
}
